import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Shield, Brain, Heart, AlertTriangle, CreditCard, RefreshCw, Mail } from "lucide-react";
import Footer from "../components/Footer";

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="terms-page">
      {/* Navigation */}
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="rounded-full text-[#6E6A66] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <span 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Terms & Care
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="animate-fade-in">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-[#A87C6D]" />
            </div>
            <h1 
              className="text-3xl md:text-4xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Terms & Care
            </h1>
            <p className="text-[#6E6A66] font-light max-w-xl mx-auto leading-relaxed">
              The Soft Ritual is built on trust, discretion, and respect for your inner life.
              This page explains how your information is handled, what the app can and cannot offer, 
              and how subscriptions work—clearly and without unnecessary complexity.
            </p>
          </div>

          {/* Privacy & Data Use */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#7A9E7E]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Privacy & Data Use
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8">
              <p className="text-[#2D2A26] font-medium mb-4">Your privacy matters here.</p>
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                The Soft Ritual collects only the information needed to operate the app and provide a supportive experience. This may include:
              </p>
              <ul className="list-disc list-inside text-[#6E6A66] font-light mb-6 space-y-2 pl-2">
                <li>basic account information (such as email address),</li>
                <li>text you voluntarily share during sessions,</li>
                <li>high-level usage data to improve the app.</li>
              </ul>
              <div className="space-y-3 text-[#6E6A66] font-light leading-relaxed">
                <p><strong className="text-[#2D2A26]">We do not sell your data.</strong></p>
                <p><strong className="text-[#2D2A26]">We do not use your content for advertising.</strong></p>
                <p><strong className="text-[#2D2A26]">We do not share personal session content with third parties.</strong></p>
                <p className="mt-4">
                  Your inputs are used solely to support your experience within The Soft Ritual and to help the app respond more thoughtfully over time.
                </p>
                <p>
                  We take reasonable measures to protect your information and limit access to it. However, no digital system can be guaranteed to be completely secure.
                </p>
                <p className="italic text-[#A8A4A0]">
                  By using The Soft Ritual, you acknowledge and accept this standard level of risk inherent to online services.
                </p>
              </div>
            </div>
          </section>

          {/* Use of AI */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#7D98A1]/10 flex items-center justify-center">
                <Brain className="w-5 h-5 text-[#7D98A1]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Use of AI
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8">
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                The Soft Ritual uses artificial intelligence to generate responses based on what you choose to share.
              </p>
              <p className="text-[#2D2A26] font-medium mb-3">AI responses are:</p>
              <ul className="list-disc list-inside text-[#6E6A66] font-light mb-6 space-y-2 pl-2">
                <li>reflective, not directive,</li>
                <li>supportive, not prescriptive,</li>
                <li>designed for emotional awareness—not diagnosis or decision-making on your behalf.</li>
              </ul>
              <p className="text-[#6E6A66] font-light leading-relaxed">
                AI responses may not always be perfect, complete, or applicable to your situation. You are encouraged to use your own judgment when engaging with the app.
              </p>
            </div>
          </section>

          {/* Not Medical, Mental Health, or Legal Advice */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#C8553D]/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#C8553D]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Not Medical, Mental Health, or Legal Advice
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8 border-l-4 border-[#C8553D]/30">
              <p className="text-[#2D2A26] font-medium mb-4">
                The Soft Ritual is not a substitute for medical care, mental health treatment, legal advice, or professional services of any kind.
              </p>
              <p className="text-[#6E6A66] font-light mb-3">The app does not:</p>
              <ul className="list-disc list-inside text-[#6E6A66] font-light mb-6 space-y-2 pl-2">
                <li>diagnose conditions,</li>
                <li>provide therapy,</li>
                <li>offer medical or psychiatric treatment,</li>
                <li>give legal, financial, or professional advice.</li>
              </ul>
              <p className="text-[#6E6A66] font-light leading-relaxed mb-4">
                If you are experiencing a mental health crisis, thoughts of self-harm, or are in immediate danger, please seek support from a qualified professional or local emergency resources.
              </p>
              <p className="text-[#A8A4A0] font-light italic">
                Using The Soft Ritual does not create a therapist-client, doctor-patient, or advisor-client relationship.
              </p>
            </div>
          </section>

          {/* Personal Responsibility */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-[#A87C6D]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Personal Responsibility
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8">
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                The Soft Ritual is a tool for reflection and self-awareness.
              </p>
              <p className="text-[#2D2A26] font-medium mb-3">You remain fully responsible for:</p>
              <ul className="list-disc list-inside text-[#6E6A66] font-light mb-6 space-y-2 pl-2">
                <li>your choices,</li>
                <li>your actions,</li>
                <li>how you interpret and apply any insights or suggestions.</li>
              </ul>
              <p className="text-[#6E6A66] font-light leading-relaxed">
                By using the app, you agree that The Soft Ritual, its creators, and its operators are not liable for decisions or outcomes resulting from your use of the app.
              </p>
            </div>
          </section>

          {/* Subscription & Payments */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#7A9E7E]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Subscription & Payments
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8">
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                Some features of The Soft Ritual may require a paid subscription.
              </p>
              <p className="text-[#2D2A26] font-medium mb-3">Subscription details:</p>
              <ul className="list-disc list-inside text-[#6E6A66] font-light space-y-2 pl-2">
                <li>Pricing and billing frequency are clearly presented before purchase.</li>
                <li>Subscriptions renew automatically unless canceled before the renewal date.</li>
                <li>You can manage or cancel your subscription through your account settings or the platform where you subscribed.</li>
                <li>Refunds, if offered, are subject to the policies of the platform used for purchase (such as the App Store or payment provider).</li>
              </ul>
            </div>
          </section>

          {/* Changes & Updates */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#7D98A1]/10 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-[#7D98A1]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Changes & Updates
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8">
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                The Soft Ritual may update features, policies, or pricing over time.
              </p>
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                When material changes are made, we will make reasonable efforts to communicate them clearly within the app or on our website.
              </p>
              <p className="text-[#A8A4A0] font-light italic">
                Continued use of the app after updates indicates acceptance of the revised terms.
              </p>
            </div>
          </section>

          {/* Contact & Questions */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#A87C6D]/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#A87C6D]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Contact & Questions
              </h2>
            </div>
            <div className="paper-card rounded-2xl p-6 md:p-8">
              <p className="text-[#6E6A66] font-light mb-4 leading-relaxed">
                If you have questions about privacy, subscriptions, or use of the app, you can contact us at:
              </p>
              <a 
                href="mailto:thesoftritual@gmail.com" 
                className="text-[#A87C6D] hover:underline font-medium"
              >
                thesoftritual@gmail.com
              </a>
              <p className="text-[#6E6A66] font-light mt-4 leading-relaxed">
                We aim to respond thoughtfully and within a reasonable timeframe.
              </p>
            </div>
          </section>

          {/* Closing Note */}
          <section className="text-center py-8 border-t border-[#E6E2DD]">
            <h2 
              className="text-xl font-normal text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Closing Note
            </h2>
            <p className="text-[#6E6A66] font-light leading-relaxed max-w-xl mx-auto mb-4">
              The Soft Ritual is designed to support presence, not replace discernment.
            </p>
            <p className="text-[#6E6A66] font-light leading-relaxed max-w-xl mx-auto mb-6">
              We encourage you to use the app in a way that feels respectful of your needs, your boundaries, and your well-being.
            </p>
            <p 
              className="text-[#A87C6D] font-light italic"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Thank you for choosing to engage with The Soft Ritual.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
